import React from "react";
import ExcelJS from "exceljs";
import { parseCustomDate } from "./ExcelVettingReport";

const ExcelSubReport = ({ data }) => {
    const exportExcelFile = async () => {
        if (!Array.isArray(data) || data.length === 0) {
            console.error("Data is not a valid array or is empty:", data);
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Define columns with headers
        worksheet.columns = [
            { header: 'S.No', key: 'serial', width: 10 },
            { header: 'File ID', key: 'file_id', width: 15 },
            { header: 'Bank Name', key: 'bank_name', width: 40 },                                       
            { header: 'Bank Branch Name', key: 'branch_name', width: 30 },
            { header: 'LH Branch Name', key: 'law_hand_branch', width: 30 },
            { header: 'Product Name', key: 'product_name', width: 30 },
            { header: 'File Location', key: 'file_location', width: 30 },
            { header: 'Assigned To', key: 'assigned_to', width: 30 },
            { header: 'Position', key: 'position', width: 30 },
            { header: 'Total count', key: 'total_count', width: 30 },
            { header: 'File Grade', key: 'file_grade', width: 30 },
            { header: 'File Type', key: 'file_type', width: 30 }, // Changed from 'file_Type' to 'file_type' for consistency
            { header: 'Login By', key: 'login_by', width: 30 },
            { header: 'Login Date', key: 'date_time', width: 30 },
            { header: 'Updated Date', key: 'updated_time', width: 30 },
        ];

        // Apply alignment and color to header cells
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF00' }, // Yellow color
            };
            cell.font = { bold: true, size: 14 };
        });
        headerRow.commit();

        // Helper function to safely parse dates
        const safeParseDate = (dateValue) => {
            if (!dateValue) return "";
            
            try {
                // Check if the date string includes time part
                const dateString = String(dateValue);
                if (dateString.includes(' ')) {
                    // If it has both date and time, use the custom parser
                    return parseCustomDate(dateString);
                } else {
                    // If it's just date, format it properly for Excel
                    const [day, month, year] = dateString.split('-').map(Number);
                    return new Date(Date.UTC(year, month - 1, day));
                }
            } catch (error) {
                console.error("Error parsing date:", dateValue, error);
                return dateValue; // Return original value if parsing fails
            }
        };

        // Helper function to determine file type based on grade
        const getFileType = (grade) => {
            if (!grade) return "";
            // Check for both 'E' and 'É' (with accent)
            if (grade === 'E') {
                return 'Emergent';
            }
            return "Normal";
        };

        const flattenedData = data.map((report, index) => {
            return {
                serial: index + 1,
                file_id: report.file_id || "",
                bank_name: report.bank_name || "",
                branch_name: report.branch_name || "",               
                law_hand_branch: report.law_hand_branch || "",
                product_name: report.product_name || "",
                file_location: report.file_location || "",
                assigned_to: report.assigned_to || "",
                position: report.position || "",
                total_count: report.total_count || 0,
                file_grade: report.file_grade || "",
                file_type: getFileType(report.file_grade), // Using the helper function
                login_by: report.login_by || "",
                date_time: report.login_date, // Using safeParseDate for dates
                updated_time: report.updated_date, // Using safeParseDate for dates
            };
        });

        // Add rows to worksheet
        flattenedData.forEach((item) => {
            const row = worksheet.addRow(item);
            
            // Apply formatting to each cell in the row
            row.eachCell((cell, colNumber) => {
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                
                // Date formatting for specific columns
                // Adjust column numbers based on your actual column indices:
                // 1: S.No, 2: File ID, 3: Bank Name, 4: Branch Name, 5: LH Branch, 
                // 6: Product, 7: Location, 8: Assigned To, 9: Position, 10: Total count,
                // 11: File Grade, 12: File Type, 13: Login By, 14: Login Date, 15: Updated Date
                
                // Login Date (column 14) and Updated Date (column 15)
                if (colNumber === 14 || colNumber === 15) {
                    if (cell.value) {
                        cell.numFmt = 'dd-mm-yyyy hh:mm:ss';
                    }
                }
                
                // Number formatting for Total count (column 10)
                if (colNumber === 10) {
                    cell.numFmt = '0';
                }
                
                // Optional: Add conditional formatting for File Type (column 12)
                if (colNumber === 12 && cell.value === 'Emergent') {
                    cell.font = { bold: true, color: { argb: 'FF9C27B0' } }; // Purple color for Emergent
                }
            });
            
            row.commit();
        });

        // Auto-filter for better data navigation
        worksheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: 1, column: worksheet.columnCount }
        };

        const currentDate = new Date();
        const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getFullYear()}`;
        
        // Generate Excel file buffer
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Queue Files Report- ${formattedDate}.xlsx`;
        anchor.click();
        
        // Clean up
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 100);
    };

    return (
        <button className="excel-download-btn" onClick={exportExcelFile}>
            Download Queue Excel
        </button>
    );
};

export default ExcelSubReport;